// Retiring Booking's customer portal (rentals and sponsorships plan,
// Phase 8; ADR 0047). Renters and sponsors moved to myBRE in Phase 3; Phase
// 8 is the end of the parallel run (§6.8, decision 20). Booking works out
// whether its portal can retire; Hub shows Booking's answer to staff with
// its time and never claims "ready" on its own.
//
// Booking's reads (bearer-only on HUB_READ_SECRET, SELECT only):
//   GET /api/internal/hub/v1/portal-retirement           switches, parity checklist, email links, readiness
//   GET /api/internal/hub/v1/portal-retirement/invites   who would get a myBRE invite now (the batch preview)
//
// And one command, through the usual command boundary:
//   booking.mybre.invite_batch { previewHash, max? }  sends myBRE invites to
//   customers with business in the next 90 days who are neither linked nor
//   invited. Booking re-checks the preview (STALE when it changed), sends at
//   most 25 per command, at most one batch per 5 minutes (LIMIT), skips
//   anyone invited in the last 60 days, and replays its first answer for the
//   same commandId.

import { z } from 'zod'

export const BOOKING_PORTAL_RETIREMENT_PATH = '/portal-retirement'
export const BOOKING_PORTAL_INVITES_PATH = '/portal-retirement/invites'

// Decision 20: at least 60 days of parallel run.
export const PARALLEL_RUN_REQUIRED_DAYS = 60
// Booking's cap per batch command.
export const INVITE_BATCH_CAP = 25

const Id = z.string().min(1).max(64)
const Iso = z.string().max(40)
const Text = (max: number) => z.string().max(max)

export const PARITY_STATUSES = ['mybre', 'retired', 'kept'] as const
export type ParityStatus = (typeof PARITY_STATUSES)[number]

export const RETIREMENT_CRITERIA = ['parallel_run', 'sponsor_cycle', 'customers_linked', 'mybre_proven', 'portal_quiet', 'support_cases', 'owner_go'] as const
export type RetirementCriterionKey = (typeof RETIREMENT_CRITERIA)[number]

export const BookingPortalRetirement = z.object({
  version: z.literal(1),
  asOf: Iso,
  switches: z.object({ mybreChannel: z.boolean(), mybreLinks: z.boolean(), mybreNotify: z.boolean(), portalRetired: z.boolean() }),
  parity: z.object({
    routes: z
      .array(
        z.object({
          route: Text(300),
          audience: z.enum(['renter', 'sponsor', 'artist', 'public', 'any']),
          status: z.enum(PARITY_STATUSES),
          mybre: Text(300).nullable(),
          reason: Text(1000).nullable(),
        }),
      )
      .max(300),
    counts: z.object({ mybre: z.number().int().min(0), retired: z.number().int().min(0), kept: z.number().int().min(0) }),
  }),
  emailLinks: z
    .array(z.object({ key: Text(80), label: Text(300), sentBy: z.enum(['web', 'cron']), linksTo: z.enum(['mybre', 'portal', 'booking']) }))
    .max(200),
  readiness: z.object({
    ready: z.boolean(),
    criteria: z
      .array(
        z.object({
          key: z.enum(RETIREMENT_CRITERIA),
          label: Text(300),
          // null: the owner's call, not something Booking can count.
          met: z.boolean().nullable(),
          detail: Text(2000),
        }),
      )
      .max(20),
    parallelRun: z.object({ startedAt: Iso.nullable(), days: z.number().int().min(0), requiredDays: z.number().int().min(0) }),
    sponsorCycle: z.object({ monthlySponsorships: z.number().int().min(0), invoicedSinceStart: z.number().int().min(0), passed: z.boolean() }),
    customers: z.object({
      withBusiness: z.number().int().min(0),
      linked: z.number().int().min(0),
      invited: z.number().int().min(0),
      neither: z.number().int().min(0),
      list: z
        .array(
          z.object({
            kind: z.enum(['rental', 'sponsorship']),
            id: Id,
            name: Text(300),
            emailMasked: Text(320),
            nextDate: Text(40).nullable(),
            state: z.enum(['linked', 'invited', 'neither']),
          }),
        )
        .max(2000),
    }),
    mybreFirsts: z.object({ payment: Iso.nullable(), signing: Iso.nullable(), upload: Iso.nullable() }),
    portal: z.object({
      actions14d: z.number().int().min(0),
      views14d: z.number().int().min(0),
      lastActionAt: Iso.nullable(),
      byRoute: z.array(z.object({ route: Text(300), actions: z.number().int().min(0), views: z.number().int().min(0) })).max(300),
      redirects30d: z.number().int().min(0),
    }),
  }),
})
export type BookingPortalRetirement = z.infer<typeof BookingPortalRetirement>
export type BookingRetirementCriterion = BookingPortalRetirement['readiness']['criteria'][number]
export type BookingParityRoute = BookingPortalRetirement['parity']['routes'][number]

export const BookingInvitePreview = z.object({
  version: z.literal(1),
  asOf: Iso,
  mybreLinks: z.boolean(),
  previewHash: z.string().min(1).max(128),
  cap: z.number().int().min(0).max(1000),
  total: z.number().int().min(0),
  recipients: z
    .array(
      z.object({
        email: Text(320),
        emailMasked: Text(320),
        name: Text(300).nullable(),
        records: z.array(z.object({ kind: z.enum(['rental', 'sponsorship']), id: Id, title: Text(300), nextDate: Text(40).nullable() })).max(50),
      }),
    )
    .max(2000),
})
export type BookingInvitePreview = z.infer<typeof BookingInvitePreview>

// What booking.mybre.invite_batch answers in `result.batch`.
export const BookingInviteBatchResult = z.object({
  sent: z.number().int().min(0),
  failed: z.number().int().min(0),
  skipped: z.number().int().min(0),
  remaining: z.number().int().min(0),
  items: z
    .array(z.object({ emailMasked: Text(320), status: z.enum(['sent', 'failed', 'skipped']), reason: Text(1000).nullable() }))
    .max(100),
})
export type BookingInviteBatchResult = z.infer<typeof BookingInviteBatchResult>

// ------------------------------------------------------------- Hub's view

// Hub never says "ready" for Booking. Booking's own verdict is shown when
// Booking answered; anything else is "unknown" (rule 11: missing data is
// not a pass).
export type RetirementVerdict = 'checks_pass' | 'not_ready' | 'unknown'

// Booking counts what it can; the owner decides the rest (support cases and
// the go). So the best Hub ever says is "Booking's checks pass; the owner
// decides", and only when Booking answered, said ready, and no counted
// criterion is unmet.
export function retirementVerdict(read: { status: string; data?: BookingPortalRetirement }): RetirementVerdict {
  if (read.status !== 'ok' || !read.data) return 'unknown'
  const r = read.data.readiness
  if (r.criteria.length === 0) return 'unknown'
  if (!r.ready || r.criteria.some((c) => c.met === false)) return 'not_ready'
  return 'checks_pass'
}

export type CriterionState = 'met' | 'not_met' | 'owner'
export const criterionState = (c: { met: boolean | null }): CriterionState => (c.met === null ? 'owner' : c.met ? 'met' : 'not_met')

// What staff see for each Booking switch.
export const RETIREMENT_SWITCH_LABEL: Record<keyof BookingPortalRetirement['switches'], { name: string; on: string; off: string }> = {
  mybreChannel: { name: 'MYBRE_CHANNEL', on: 'myBRE can reach Booking for renters and sponsors.', off: 'myBRE can’t reach Booking yet: renters and sponsors use Booking’s portal.' },
  mybreLinks: { name: 'MYBRE_LINKS', on: 'Booking’s emails link to myBRE.', off: 'Booking’s emails still link to its own portal.' },
  mybreNotify: { name: 'MYBRE_NOTIFY', on: 'Booking pushes notices to myBRE.', off: 'Booking sends no pushes to myBRE.' },
  portalRetired: { name: 'PORTAL_RETIRED', on: 'Booking’s portal answers only with redirects into myBRE.', off: 'Booking’s portal still works beside myBRE.' },
}
